import React from "react";
import crownImg from "@/assets/spotlight-crown.png";

/**
 * SpotlightCrown — a photorealistic 3D gold crown that sits atop a
 * Spotlight seller's avatar. Uses a high-fidelity PNG art asset
 * (jeweled gold crown) supplied by the product owner.
 *
 * The PNG is imported through webpack so the URL resolves correctly
 * in both dev and prod builds. We inject that URL into a CSS
 * variable (`--crown-img`) so the masked gold-reflection overlay in
 * index.css can reference the SAME image as its mask source.
 *
 * Animations layered on top of the static asset:
 *   • **Bob + tilt** — the crown gently rocks ±1.5° and floats ±3px
 *     on a 3.6s loop so it never looks pasted on.
 *   • **Specular gold reflection** — a bright, narrow catch-light band
 *     sweeps across the crown's gold every 2.8s, masked to the PNG's
 *     alpha so the highlight ONLY paints on the metal (not the
 *     transparent backdrop). This is what sells "real gold" — light
 *     moving across a 3D metal surface.
 *   • **Diamond twinkles** — 10 small white starbursts twinkle at
 *     fixed points over the crown so the gems read as actively
 *     catching light.
 */
export default function SpotlightCrown({ size = 70 }) {
  // 10 sparkle dots positioned near where the crown's diamonds live
  // (top diamond, fleurs, ring of stones along the band). Each gets
  // its own delay so the twinkle is continuous.
  const SPARKLES = [
    { top: "8%",  left: "50%", delay: "0.0s", dur: "1.6s", scale: 1.6 }, // top diamond
    { top: "32%", left: "33%", delay: "0.4s", dur: "2.0s", scale: 1.2 },
    { top: "32%", left: "67%", delay: "0.8s", dur: "1.8s", scale: 1.2 },
    { top: "48%", left: "50%", delay: "0.2s", dur: "2.2s", scale: 1.4 },
    { top: "62%", left: "22%", delay: "1.1s", dur: "1.9s", scale: 1.0 },
    { top: "62%", left: "78%", delay: "0.6s", dur: "2.1s", scale: 1.0 },
    { top: "78%", left: "38%", delay: "1.4s", dur: "1.7s", scale: 1.0 },
    { top: "78%", left: "62%", delay: "0.3s", dur: "1.8s", scale: 1.0 },
    { top: "88%", left: "28%", delay: "0.9s", dur: "2.4s", scale: 0.9 },
    { top: "88%", left: "72%", delay: "1.6s", dur: "2.0s", scale: 0.9 },
  ];

  return (
    <div
      className="spotlight-crown-img-wrap"
      style={{
        width: size,
        height: size * 0.74, // crown PNG aspect ratio ≈ 468:348
        position: "relative",
        pointerEvents: "none",
        // Expose the PNG URL to CSS so the reflection layer can mask to it.
        "--crown-img": `url(${crownImg})`,
      }}
      aria-hidden="true"
    >
      {/* Bob+tilt wrapper so we can layer a separate reflection without
          inheriting the rocking transform. */}
      <div className="spotlight-crown-bob" style={{ width: "100%", height: "100%" }}>
        {/* Base layer — the photoreal gold crown. */}
        <img
          src={crownImg}
          alt=""
          draggable={false}
          className="spotlight-crown-base"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            userSelect: "none",
            filter: "drop-shadow(0 5px 10px rgba(180, 110, 20, 0.55))",
          }}
        />

        {/* Specular gold reflection — bright catch-light band sweeping
            diagonally across the crown, masked to the PNG silhouette. */}
        <span className="spotlight-crown-reflection" aria-hidden="true" />

        {/* Warm glow halo behind the crown — sells the "real gold
            radiating light" feel. */}
        <span className="spotlight-crown-glow" aria-hidden="true" />
      </div>

      {/* Diamond twinkles — sit ABOVE the crown so the starbursts
          punch through the metal. */}
      <div className="spotlight-crown-sparkles">
        {SPARKLES.map((s, i) => (
          <span
            key={i}
            className="spotlight-crown-sparkle"
            style={{
              top: s.top,
              left: s.left,
              animationDelay: s.delay,
              animationDuration: s.dur,
              "--sparkle-scale": s.scale,
            }}
          />
        ))}
      </div>
    </div>
  );
}
